import clsx from 'clsx';
import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon/Icon';
import useBreadcrumbs from './useBreadcrumbs';
import type { BreadcrumbItem, ResolvedBreadcrumbItem } from './useBreadcrumbs';
import './_breadcrumbs.scss';

export type { BreadcrumbItem } from './useBreadcrumbs';

export type BreadcrumbsProps = {
  ariaLabel?: string;
  className?: string;
  items: BreadcrumbItem[];
};

function BreadcrumbContent({ item }: Readonly<{ item: ResolvedBreadcrumbItem }>): JSX.Element {
  return (
    <span className="ui-breadcrumbs__content">
      {item.icon && (
        <span aria-hidden="true" className="ui-breadcrumbs__icon">
          <Icon color={item.iconColor ?? 'default'} name={item.icon} size="sm" />
        </span>
      )}
      {item.text}
    </span>
  );
}

function renderBreadcrumbItem(item: ResolvedBreadcrumbItem): JSX.Element {
  if (item.isInternalLink && item.href) {
    return (
      <Link className="ui-breadcrumbs__link" to={item.href}>
        <BreadcrumbContent item={item} />
      </Link>
    );
  }

  if (item.isLink && item.href) {
    return (
      <a className="ui-breadcrumbs__link" href={item.href} rel="noopener noreferrer" target="_blank">
        <BreadcrumbContent item={item} />
      </a>
    );
  }

  return (
    <span
      aria-current={item.isLast ? 'page' : undefined}
      aria-disabled={item.isDisabled || undefined}
      className="ui-breadcrumbs__text"
    >
      <BreadcrumbContent item={item} />
    </span>
  );
}

function Breadcrumbs({ ariaLabel, className, items }: Readonly<BreadcrumbsProps>): JSX.Element | null {
  const { resolvedAriaLabel, resolvedItems } = useBreadcrumbs({ ariaLabel, items });

  if (resolvedItems.length === 0) return null;

  return (
    <nav aria-label={resolvedAriaLabel} className={clsx('ui-breadcrumbs', className)}>
      <ol className="ui-breadcrumbs__list">
        {resolvedItems.map((item) => (
          <li className="ui-breadcrumbs__item" key={item.key}>
            {renderBreadcrumbItem(item)}
            {!item.isLast && (
              <span aria-hidden="true" className="ui-breadcrumbs__separator">
                /
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
